import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@gettactic/components";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { Button } from "@gettactic/components/src/primitives/button";
import { Switch } from "@gettactic/components/src/primitives/Switch";
import {
  ArrowLeft,
  Database,
  Eye,
  Image,
  Maximize,
  MoreHorizontal,
  Pencil,
  Ruler,
  Upload,
  X
} from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { SpotShape } from "../../../../types";
import {
  computeShapesBounds,
  FIT_PADDING,
  shiftShapes
} from "../../../../utils/computeShapesBounds";
import { convertSvgToMapCanvasValue } from "../../../../utils/svgImport";
import { EditorField } from "../../../ui/EditorField";
import { IconButton } from "../../../ui/IconButton";
import { TooltipProvider } from "../../../ui/tooltip";
import {
  useMapCanvas,
  useMapCanvasStage
} from "../../contexts/MapCanvasContext";
import type {
  MapCanvasValue,
  MapSaveOptions
} from "../../contexts/MapCanvasContext/MapCanvasContext.types";
import {
  useMapEditorActions,
  useMapEditorProps,
  useMapEditorSelection,
  useMapEditorUI
} from "../../contexts/MapEditorContext";
import { EDITING_ZONE_MAX_HEIGHT, EDITING_ZONE_MAX_WIDTH } from "../../defines";
import { useShapeMetrics } from "../../helpers/shapeMetrics";
import { EditorTooltip } from "../controls/EditorTooltip";
import { exportMapAsPng } from "../helpers/exportMap";
import { useUploadFloorPlan } from "../helpers/uploadFloorPlan";
import { SpotsManager } from "../spots/SpotsManager";
import { AutoImportDialog } from "./AutoImportDialog";
import { CopyMapFromFloor } from "./CopyMapFromFloor";
import { MapVersionHistory } from "./MapVersionHistory";
import { SaveMapDialog } from "./SaveMapDialog";
import { NumberingPreviewTool } from "./tools/NumberingPreviewTool";

const Divider: React.FC = () => (
  <div className="mx-1.5 h-5 w-px flex-none bg-neutral-200" />
);

export const TopToolbar: React.FC = () => {
  const snapToGridId = React.useId();
  const showGridId = React.useId();
  const mapCanvas = useMapCanvas();
  const { centerStage } = useMapCanvasStage();
  const floorInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);
  const legacySvgInputRef = React.useRef<HTMLInputElement>(null);
  const {
    setEditingResources,
    setSnapToGrid,
    toggleGrid,
    save: handleSave,
    openScalePrompt,
    togglePreview
  } = useMapEditorActions();
  const { selectedIds } = useMapEditorSelection();
  const {
    editingResources,
    onExit,
    title,
    titlePrefix,
    headerSlot,
    restrictedMode,
    saveModes
  } = useMapEditorProps();
  const { snapToGrid, showGrid, isDirty, isPreview } = useMapEditorUI();
  const uploadFloorPlan = useUploadFloorPlan();
  const metrics = useShapeMetrics();

  const [isSpotsManagerOpen, setIsSpotsManagerOpen] = React.useState(false);
  const [importMode, setImportMode] = React.useState<"ai" | "json">("ai");
  const [isAutoImportOpen, setIsAutoImportOpen] = React.useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = React.useState(false);
  const [pendingLegacyImport, setPendingLegacyImport] =
    React.useState<MapCanvasValue | null>(null);

  const handleSaveConfirm = async (options: MapSaveOptions) => {
    setIsSaving(true);
    try {
      const isSaved = await handleSave(options);
      if (isSaved) setIsSaveDialogOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetEditingResources = () => {
    if (editingResources?.isOpen) {
      setEditingResources({
        isOpen: false,
        resourceIds: []
      });
      return;
    }
    const currentShapes = mapCanvas.shapesRef.current;
    const resourceIds = selectedIds
      .filter((id) => {
        const shape = currentShapes.find((shape) => shape.id === id);
        return shape?.type === "spot" && shape?.resource_id;
      })
      .map(
        (id) =>
          (
            currentShapes.find((shape) => shape.id === id) as
              | SpotShape
              | undefined
          )?.resource_id
      ) as string[];

    setEditingResources({
      isOpen: true,
      resourceIds
    });
  };

  const handleFloorFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = e.target.files?.[0];
      await uploadFloorPlan(file);
      if (floorInputRef.current) {
        floorInputRef.current.value = "";
      }
    } catch (_error) {
      toast.error("Error uploading floor plan");
    }
  };

  // Converts a legacy SVG map (the map_url format) into builder shapes and
  // loads the result as the canvas content — same conversion that runs
  // automatically when a floor has only a legacy map.
  const applyLegacyImport = (
    converted: MapCanvasValue,
    keepResourceLinks: boolean
  ) => {
    const value = keepResourceLinks
      ? converted
      : {
          ...converted,
          shapes: converted.shapes.map((shape) =>
            shape.type === "spot" ? { ...shape, resource_id: undefined } : shape
          )
        };
    mapCanvas.loadMapValue(value);
    // After the shapes commit: normalize margins/canvas size (same as
    // the toolbar's Fit button), then re-center the stage on the result —
    // the previous pan/zoom may point far away from the converted map.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        handleFitCanvas();
        requestAnimationFrame(() => centerStage());
      });
    });
    toast.success(`Converted ${value.shapes.length} shapes`);
  };

  const handleLegacySvgFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (legacySvgInputRef.current) {
      legacySvgInputRef.current.value = "";
    }
    if (!file) return;
    try {
      const converted = convertSvgToMapCanvasValue(await file.text());
      const linkedSpots = converted.shapes.filter(
        (shape) => shape.type === "spot" && shape.resource_id
      ).length;
      if (linkedSpots > 0) {
        // Let the user decide whether the imported spots keep pointing at
        // the existing resources or come in unlinked.
        setPendingLegacyImport(converted);
        return;
      }
      applyLegacyImport(converted, true);
    } catch (_error) {
      toast.error("Couldn't convert this SVG");
    }
  };

  const handleExportPng = async () => {
    const exportOptions = {
      width: mapCanvas.editingZoneWidth,
      height: mapCanvas.editingZoneHeight
    };
    const ok = metrics?.exportPng
      ? await metrics.exportPng(exportOptions)
      : await exportMapAsPng(exportOptions);
    if (!ok) {
      toast.error("Couldn't export the map");
    }
  };

  const handleBackgroundFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = e.target.files?.[0];
      const extension = file?.name.split(".").pop();
      await uploadFloorPlan(file, {
        imageType: extension === "jpg" || extension === "jpeg" ? "jpg" : "png",
        type: "background",
        replaceAll: false,
        resizeEditingZone: false
      });
      if (floorInputRef.current) {
        floorInputRef.current.value = "";
      }
    } catch (_error) {
      toast.error("Error uploading background");
    }
  };

  const handleFitCanvas = () => {
    const shapes = mapCanvas.shapesRef.current;
    const bounds = computeShapesBounds(shapes, 0);
    if (!bounds) return;

    // Shift shapes so content starts at (FIT_PADDING, FIT_PADDING)
    const dx = FIT_PADDING - bounds.x;
    const dy = FIT_PADDING - bounds.y;
    if (dx !== 0 || dy !== 0) {
      mapCanvas.setShapes(shiftShapes(shapes, dx, dy));
    }

    mapCanvas.updateEditingZoneSize(
      Math.min(
        Math.ceil(bounds.width + FIT_PADDING * 2),
        EDITING_ZONE_MAX_WIDTH
      ),
      Math.min(
        Math.ceil(bounds.height + FIT_PADDING * 2),
        EDITING_ZONE_MAX_HEIGHT
      )
    );
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex w-full items-center gap-1 border-neutral-200 border-b bg-background-primary px-2">
        <input
          ref={floorInputRef}
          type="file"
          accept=".svg"
          onChange={handleFloorFileChange}
          className="hidden"
        />
        <input
          ref={backgroundInputRef}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={handleBackgroundFileChange}
          className="hidden"
        />
        <input
          ref={legacySvgInputRef}
          type="file"
          accept=".svg"
          onChange={handleLegacySvgFileChange}
          className="hidden"
        />

        {/* Left: exit, map identity, save state, history */}
        {onExit && (
          <>
            <IconButton
              label="Exit map builder"
              onClick={() => {
                // No unsaved changes? Exit without prompting.
                if (!isDirty) {
                  onExit();
                  return;
                }
                setIsExitConfirmOpen(true);
              }}
            >
              <ArrowLeft size={17} />
            </IconButton>
            <Divider />
          </>
        )}
        {headerSlot ? (
          <div className="flex min-w-0 items-center gap-1 pl-1">
            {headerSlot}
          </div>
        ) : (
          <>
            {titlePrefix ? (
              <span className="max-w-40 flex-none truncate pl-1 text-neutral-400 text-sm">
                {titlePrefix} /
              </span>
            ) : undefined}
            <span className="max-w-56 truncate px-1 font-semibold text-neutral-900 text-sm">
              {title || "Map editor"}
            </span>
          </>
        )}
        <span
          className={
            isDirty
              ? "flex-none rounded-full bg-amber-50 px-2 py-0.5 font-medium text-[11px] text-amber-600"
              : "flex-none rounded-full bg-green-50 px-2 py-0.5 font-medium text-[11px] text-green-600"
          }
        >
          {isDirty ? "Unsaved changes" : "Saved"}
        </span>
        {!restrictedMode && (
          <>
            <MapVersionHistory />
            <CopyMapFromFloor />
          </>
        )}

        <div className="min-w-0 flex-1" />

        {/* Right: import/export, canvas size, view toggles, tools, save */}
        {!restrictedMode && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton
                  label="Import floor plan or background"
                  hasTooltip={false}
                >
                  <Upload size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {mapCanvas.canRebuildWithAi && (
                  <DropdownMenuItem
                    onClick={() => {
                      setImportMode("ai");
                      setIsAutoImportOpen(true);
                    }}
                  >
                    Rebuild with AI (image/PDF)
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    setImportMode("json");
                    setIsAutoImportOpen(true);
                  }}
                >
                  Saved map (JSON)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => floorInputRef.current?.click()}
                >
                  Floor Plan (SVG)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => backgroundInputRef.current?.click()}
                >
                  Background (PNG, JPG)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => legacySvgInputRef.current?.click()}
                >
                  Legacy Map (SVG, convert)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AutoImportDialog
              key={importMode}
              importMode={importMode}
              open={isAutoImportOpen}
              onOpenChange={setIsAutoImportOpen}
            />
            <IconButton label="Export as PNG" onClick={handleExportPng}>
              <Image size={16} />
            </IconButton>
            {togglePreview ? (
              <IconButton
                label={isPreview ? "Back to editing" : "Preview as on the map"}
                isActive={!!isPreview}
                onClick={togglePreview}
                data-preview-toggle
              >
                <Eye size={16} />
              </IconButton>
            ) : null}

            <Divider />

            <div className="flex items-center gap-1">
              <EditorField
                fieldKey="W"
                label="Canvas width"
                containerClassName="w-[76px]"
                value={String(mapCanvas.editingZoneWidth)}
                onChange={(e) => {
                  mapCanvas.updateEditingZoneSize(
                    Math.min(EDITING_ZONE_MAX_WIDTH, Number(e.target.value)),
                    mapCanvas.editingZoneHeight
                  );
                }}
              />
              <EditorField
                fieldKey="H"
                label="Canvas height"
                containerClassName="w-[76px]"
                value={String(mapCanvas.editingZoneHeight)}
                onChange={(e) => {
                  mapCanvas.updateEditingZoneSize(
                    mapCanvas.editingZoneWidth,
                    Math.min(EDITING_ZONE_MAX_HEIGHT, Number(e.target.value))
                  );
                }}
              />
              <IconButton
                label="Fit canvas to drawing"
                onClick={handleFitCanvas}
              >
                <Maximize size={15} />
              </IconButton>
            </div>

            <Divider />
          </>
        )}

        <EditorTooltip message="Snap objects to the grid while moving" asChild>
          <label
            htmlFor={snapToGridId}
            className="flex h-8 flex-none cursor-pointer items-center gap-1.5 rounded-md px-2 text-neutral-600 text-xs hover:bg-neutral-100"
          >
            <Switch
              id={snapToGridId}
              checked={snapToGrid}
              onCheckedChange={setSnapToGrid}
            />
            Snap
          </label>
        </EditorTooltip>
        {snapToGrid && (
          <EditorField
            type="number"
            label="Grid size"
            containerClassName="w-14"
            value={String(mapCanvas.gridSize)}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value > 0) {
                mapCanvas.setGridSize(value);
              }
            }}
            min={1}
            max={100}
          />
        )}
        <EditorTooltip message="Show the grid" asChild>
          <label
            htmlFor={showGridId}
            className="flex h-8 flex-none cursor-pointer items-center gap-1.5 rounded-md px-2 text-neutral-600 text-xs hover:bg-neutral-100"
          >
            <Switch
              id={showGridId}
              checked={showGrid}
              onCheckedChange={toggleGrid}
            />
            Grid
          </label>
        </EditorTooltip>

        {!restrictedMode && (
          <>
            <Divider />

            <NumberingPreviewTool />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton label="More options" hasTooltip={false}>
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => setIsSpotsManagerOpen(true)}>
                    <Database size={16} />
                    Manage Spots
                  </DropdownMenuItem>
                  {openScalePrompt ? (
                    <DropdownMenuItem onClick={openScalePrompt}>
                      <Ruler size={16} />
                      Map scale…
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    onClick={handleSetEditingResources}
                    className="flex items-center gap-1"
                  >
                    {!editingResources?.isOpen ? (
                      <>
                        <Pencil size={16} />
                        Edit resources
                      </>
                    ) : (
                      <>
                        <X size={16} />
                        Close Resources Editor
                      </>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        <Divider />

        <Button
          variant="primary"
          className="h-8 flex-none rounded-md px-3.5 font-medium text-[13px]"
          onClick={() => setIsSaveDialogOpen(true)}
        >
          Save
        </Button>

        {onExit && (
          <ConfirmModal
            size={"xl"}
            title={"Unsaved changes"}
            isOpen={isExitConfirmOpen}
            onOpenChange={setIsExitConfirmOpen}
            onConfirm={async () => {
              const isSaved = await handleSave({ mode: "activate-now" });
              if (isSaved) onExit();
            }}
            onExtra={() => {
              setIsExitConfirmOpen(false);
              onExit();
            }}
            cancelLabel={"Stay Here"}
            extraLabel={"Exit without save"}
            confirmLabel={"Save and close"}
          >
            Are you sure you want to exit the map builder? All your unsaved
            changes will be lost.
          </ConfirmModal>
        )}
        <ConfirmModal
          size={"xl"}
          title={"Keep resource links?"}
          isOpen={!!pendingLegacyImport}
          onOpenChange={(open: boolean) => {
            if (!open) setPendingLegacyImport(null);
          }}
          onConfirm={() => {
            if (pendingLegacyImport) {
              applyLegacyImport(pendingLegacyImport, true);
            }
            setPendingLegacyImport(null);
          }}
          onExtra={() => {
            if (pendingLegacyImport) {
              applyLegacyImport(pendingLegacyImport, false);
            }
            setPendingLegacyImport(null);
          }}
          confirmLabel={"Keep links"}
          extraLabel={"Import unlinked"}
          cancelLabel={"Cancel"}
        >
          {pendingLegacyImport
            ? pendingLegacyImport.shapes.filter(
                (shape) => shape.type === "spot" && shape.resource_id
              ).length
            : 0}{" "}
          imported spots are linked to existing resources. Keep those links, or
          import the spots unlinked so they can be assigned to new resources?
        </ConfirmModal>
        <SpotsManager
          isOpen={isSpotsManagerOpen}
          onOpenChange={setIsSpotsManagerOpen}
        />
        <SaveMapDialog
          open={isSaveDialogOpen}
          onOpenChange={setIsSaveDialogOpen}
          onConfirm={handleSaveConfirm}
          isSaving={isSaving}
          modes={saveModes}
        />
      </div>
    </TooltipProvider>
  );
};
